/* Realitní obchod ve složce klienta — to „něco jiného", co se objeví.
 *
 * PROČ TO NENÍ ŘÁDEK OBCHODU:
 * U hypotéky se poradce ptá „v jaké je to fázi a kolik to nese". U nemovitosti
 * se ptá jinak. Prodávající chce vědět, KOLIK LIDÍ SE PŘIŠLO PODÍVAT a co se
 * pro prodej dělá — makléřova bolest č. 3 zní doslova „nemovitost se půl roku
 * neprodá a chybí komunikace s klientem". Kupující chce vědět, JESTLI MÁ
 * REZERVACI A KDO MU UDĚLÁ HYPOTÉKU. Řádek s poskytovatelem a body neodpoví
 * ani na jedno.
 *
 * DVĚ ROLE, DVA OBSAHY, JEDEN TYP KARTY. Karta je pořád `KARTA`
 * z components/zakazky/kroky.js — stejná jako v pipeline i na kartě zakázky.
 * Mění se obsah, ne vzhled.
 *
 * NIC SE TU NEPOČÍTÁ. Fáze, dny, provize i podíl na odměně chodí ze serveru
 * (routes/reality_klient.py). Jediné, co se počítá tady, jsou dny ve fázi —
 * a to proto, že „dnes" je věc kalendáře makléře, ne serveru.
 *
 * NEZNÁMÉ NENÍ NULA. Chybějící cena, nenastavená provize ani zakázka bez
 * obchodu se nekreslí jako nula; napíše se, co se neví.
 */
import React from "react";
import { toast } from "sonner";

import { KARTA, POPISEK, nazevKroku, tlacitko } from "@/components/zakazky/kroky";
import { denLidsky } from "@/components/zakazky/datum";
import {
  barvaTeploty, dniVeFazi, popisekTeploty, teplotaFaze,
} from "@/components/zakazky/teplota";
import { useLanguage } from "@/contexts/LanguageContext";
import { zapnuto } from "@/lib/funkce";

const TYP_KLIC = { byt: "typByt", dum: "typDum", pozemek: "typPozemek", komercni: "typKomercni" };

const NADPIS_SEKCE = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--muted)",
  margin: "0 0 8px",
};

const RADKA = { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 };

/** Odkaz do schránky. Selhání se NEHLÁSÍ jako úspěch — makléř by poslal nic. */
async function zkopiruj(urlPath, t) {
  const plny = `${typeof window !== "undefined" ? window.location.origin : ""}${urlPath}`;
  try {
    await navigator.clipboard.writeText(plny);
    toast.success(t("realSlozka.zkopirovano"));
  } catch {
    toast.error(t("realSlozka.nezkopirovano"));
  }
}

/** Pás sedmi kroků. Vlastní, protože popisky kupujícího jsou jiné než prodeje. */
function Pas({ index, celkem }) {
  const posledni = Math.max(0, (Number(celkem) || 7) - 1);
  const ted = Math.max(0, Math.min(Number(index) || 0, posledni));
  return (
    <div style={{ display: "flex", gap: 3, marginTop: 8 }} aria-hidden="true">
      {Array.from({ length: posledni + 1 }, (_, i) => (
        <span
          key={i}
          style={{
            flex: 1,
            height: i === ted ? 6 : 4,
            borderRadius: 3,
            background: i <= ted ? "var(--accent)" : "var(--line)",
          }}
        />
      ))}
    </div>
  );
}

/** Řádek „popisek — hodnota". Prázdná hodnota se nekreslí vůbec. */
function Udaj({ popisek, hodnota, barva }) {
  if (hodnota === null || hodnota === undefined || hodnota === "") return null;
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 6 }}>
      <span style={{ ...POPISEK, flexShrink: 0 }}>{popisek}</span>
      <span style={{ fontSize: 13, color: barva || "var(--ink)", textAlign: "right" }}>{hodnota}</span>
    </div>
  );
}

/** Kolik dní stojíme ve fázi + jestli je to už moc. `null` = nevíme, ne nula. */
function Teplota({ stavOd, dokonceno, t }) {
  const dni = dniVeFazi(stavOd);
  const stupen = teplotaFaze(dni, { dokonceno });
  const klic = popisekTeploty(stupen);
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: barvaTeploty(stupen) }}>
      {dni == null
        ? t("realSlozka.veFaziNeznamo")
        : `${t("zakazky.sloupecStani")} ${t.plural("zakazky.den", dni)}`}
      {klic ? ` — ${t(klic)}` : ""}
    </div>
  );
}

/* ── PRODÁVAJÍCÍ ────────────────────────────────────────────────────────── */

/** Nejbližší CHYSTANÁ prohlídka; když žádná není, ta poslední proběhlá.
 *
 * Server posílá termíny vzestupně. Vzít prostě první nebo poslední by
 * znamenalo, že u nemovitosti s deseti proběhlými prohlídkami svítí ve složce
 * termín z března jako „nejbližší" — a majitel se ptá, proč se nic neděje.
 */
function prohlidkaKUkazani(seznam, ted = Date.now()) {
  const zive = (seznam || []).filter((p) => p && !p.zneplatneno && p.termin);
  if (!zive.length) return null;
  const chystane = zive.filter((p) => {
    const cas = Date.parse(p.termin);
    return Number.isFinite(cas) && cas >= ted;
  });
  return chystane.length ? chystane[0] : zive[zive.length - 1];
}

function KartaProdeje({ z, t, formatMoney, onOtevrit }) {
  const prohlidka = prohlidkaKUkazani(z.prohlidky);
  const odkaz = z.odkaz_majitele;
  return (
    <article style={{ ...KARTA, marginBottom: 12 }} data-testid={`reality-prodej-${z.zakazka_id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14, color: "var(--ink)" }}>{z.adresa || t("realSlozka.bezAdresy")}</b>
        <span style={POPISEK}>
          {t(`zakazky.${TYP_KLIC[z.typ] || "typByt"}`)}
          {z.cena != null ? ` · ${formatMoney(z.cena)}` : ` · ${t("realSlozka.cenaChybi")}`}
        </span>
      </div>

      <Pas index={z.stav_index} celkem={z.kroku_celkem} />
      <div style={{ marginTop: 6, fontSize: 13, color: "var(--ink)" }}>
        {nazevKroku(t, z.stav_index)}
        <span style={POPISEK}>{` · ${t("zakazky.krok")} ${(z.stav_index || 0) + 1}/${z.kroku_celkem || 7}`}</span>
      </div>
      {z.sirotek
        ? <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>{t("realSlozka.bezObchodu")}</div>
        : <Teplota stavOd={z.stav_od} dokonceno={z.dokonceno} t={t} />}

      <Udaj popisek={t("realSlozka.zajemcu")} hodnota={String(z.zajemcu ?? 0)} />
      <Udaj
        popisek={t("realSlozka.pristiProhlidka")}
        hodnota={prohlidka
          ? `${denLidsky(prohlidka.termin)} · ${prohlidka.prihlasenych} ${t("realSlozka.prihlaseno")}`
          : t("realSlozka.zadnaProhlidka")}
      />
      <Udaj
        popisek={t("realSlozka.provize")}
        hodnota={z.provize_odhad != null
          ? `${z.provize_pct} % · ${formatMoney(z.provize_odhad)}`
          : t("realSlozka.provizeNenastavena")}
        barva={z.provize_odhad != null ? "var(--ink)" : "var(--muted)"}
      />

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
        <div style={{ fontSize: 13, color: "var(--ink)" }}>{t("realSlozka.majitelOdkaz")}</div>
        <p style={{ margin: "2px 0 0", ...POPISEK }}>{t("realSlozka.majitelOdkazSub")}</p>
        {odkaz ? (
          <>
            <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--muted)", wordBreak: "break-all" }}>
              {odkaz.url_path}
              {odkaz.zivy ? "" : ` · ${t("realSlozka.odkazPropadly")}`}
            </p>
            <div style={RADKA}>
              <a
                href={odkaz.url_path}
                target="_blank"
                rel="noreferrer"
                style={{ ...tlacitko(), textDecoration: "none", display: "inline-block" }}
                data-testid={`reality-majitel-otevrit-${z.zakazka_id}`}
              >
                {t("realSlozka.otevrit")}
              </a>
              <button type="button" style={tlacitko()} onClick={() => zkopiruj(odkaz.url_path, t)}>
                {t("realSlozka.kopirovat")}
              </button>
            </div>
          </>
        ) : (
          <p style={{ margin: "6px 0 0", ...POPISEK }}>{t("realSlozka.majitelChybi")}</p>
        )}
      </div>

      <div style={RADKA}>
        <button
          type="button"
          style={tlacitko(true)}
          /* Rovnou na kartu OBCHODU — nemovitost tam od 9/2026 žije jako
             sekce obchodního případu. Bez `deal_id` (sirotek nebo starý
             backend) se jde přes `/zakazky/:id`, což přesměruje. */
          onClick={() => onOtevrit(z.deal_id ? `/obchody/${z.deal_id}` : `/zakazky/${z.zakazka_id}`)}
          data-testid={`reality-otevrit-zakazku-${z.zakazka_id}`}
        >
          {t("realSlozka.otevritZakazku")}
        </button>
        <button
          type="button"
          style={tlacitko()}
          onClick={() => onOtevrit(`/zakazky/${z.zakazka_id}/zajemci`)}
        >
          {t("realSlozka.zajemciAProhlidky")}
        </button>
        {/* Propočet „prodat, nebo nechat?" i tady. Ve složce klienta sedí
            poradce, který o nemovitosti mluví jako o části majetku — a majitel
            se ho na tohle zeptá dřív než makléře. */}
        <button
          type="button"
          style={tlacitko()}
          onClick={() => onOtevrit(`/prodat-nebo-nechat?zakazka=${z.zakazka_id}`)}
          data-testid={`reality-prodat-nechat-${z.zakazka_id}`}
        >
          {t("zakazky.prodatNechat")}
        </button>
        {z.inzerat && (
          <a
            href={z.inzerat.url_path}
            target="_blank"
            rel="noreferrer"
            style={{ ...tlacitko(), textDecoration: "none", display: "inline-block" }}
          >
            {z.inzerat.publikovano ? t("realSlozka.inzeratOtevrit") : t("realSlozka.inzeratSkryty")}
          </a>
        )}
      </div>
    </article>
  );
}

/* ── KUPUJÍCÍ ───────────────────────────────────────────────────────────── */

/** Popisek fáze kupujícího. Vlastní klíče — jeho cesta začíná prohlídkou. */
function nazevKrokuKoupe(t, index) {
  const i = Math.max(0, Math.min(Number(index) || 0, 6));
  return t(`realSlozka.koupeFaze${i}`);
}

function stavRezervace(r, t) {
  if (!r) return { text: t("realSlozka.rezervaceZadna"), barva: "var(--muted)" };
  if (r.podepsano_at) {
    return { text: `${t("realSlozka.rezervacePodepsana")} ${denLidsky(r.podepsano_at)}`,
             barva: "var(--positive, var(--ink))" };
  }
  return { text: t("realSlozka.rezervaceRozepsana"), barva: "var(--ink)" };
}

function KartaKoupe({ k, hypoteky, t, formatMoney, onOtevrit, onNovaHypoteka }) {
  const rez = stavRezervace(k.rezervace, t);
  const hyp = (hypoteky || []).find((h) => h.result === "open") || (hypoteky || [])[0] || null;
  const klic = k.deal_id || k.zakazka_id || "bez-id";
  return (
    <article style={{ ...KARTA, marginBottom: 12 }} data-testid={`reality-koupe-${klic}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14, color: "var(--ink)" }}>
          {k.adresa || t("realSlozka.nemovitostNeznama")}
        </b>
        {k.cena != null && <span style={POPISEK}>{formatMoney(k.cena)}</span>}
      </div>

      <Pas index={k.stav_index} celkem={k.kroku_celkem} />
      <div style={{ marginTop: 6, fontSize: 13, color: "var(--ink)" }}>
        {nazevKrokuKoupe(t, k.stav_index)}
        <span style={POPISEK}>{` · ${t("zakazky.krok")} ${(k.stav_index || 0) + 1}/${k.kroku_celkem || 7}`}</span>
      </div>
      {k.deal_id
        ? <Teplota stavOd={k.stav_od} dokonceno={k.dokonceno} t={t} />
        : <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>{t("realSlozka.koupeBezObchodu")}</div>}

      {k.zajemce && <Udaj popisek={t("realSlozka.postupZajemce")} hodnota={k.zajemce.stav_nazev} />}
      {/* Rezervační smlouva je VYPNUTÁ přepínačem (lib/funkce.js, Pan Čung
          2. 10. 2026). „Zatím žádná" by pak vybízelo hledat, kde ji sepsat. */}
      {zapnuto("rezervacniSmlouva") && (
        <Udaj popisek={t("realSlozka.rezervace")} hodnota={rez.text} barva={rez.barva} />
      )}
      <Udaj
        popisek={t("realSlozka.hypoteka")}
        hodnota={hyp ? `${hyp.title}${hyp.stav_label ? ` · ${hyp.stav_label}` : ""}` : t("realSlozka.hypotekaZadna")}
        barva={hyp ? "var(--ink)" : "var(--muted)"}
      />

      <div style={RADKA}>
        {k.deal_id && (
          <button
            type="button"
            style={tlacitko(true)}
            /* Karta KUPUJÍCÍHO, ne obecná karta obchodu. Na /obchody/:id
               není vidět, co ten člověk hledá ani co jsme mu nabídli —
               a přesně kvůli tomu do jeho složky makléř chodí. */
            onClick={() => onOtevrit(`/obchody/${k.deal_id}`)}
            data-testid={`reality-otevrit-obchod-${k.deal_id}`}
          >
            {t("realSlozka.otevritPoptavku")}
          </button>
        )}
        {zapnuto("rezervacniSmlouva") && k.zakazka_id && (
          <button
            type="button"
            style={tlacitko()}
            onClick={() => onOtevrit(`/rezervacni-smlouva?zakazka=${k.zakazka_id}`)}
            data-testid={`reality-rezervace-${klic}`}
          >
            {k.rezervace ? t("realSlozka.rezervaceOtevrit") : t("realSlozka.rezervaceSepsat")}
          </button>
        )}
        {!hyp && (
          <button type="button" style={tlacitko()} onClick={onNovaHypoteka}
                  data-testid={`reality-nova-hypoteka-${klic}`}>
            {t("realSlozka.hypotekaZalozit")}
          </button>
        )}
        {k.inzerat && (
          <a href={k.inzerat.url_path} target="_blank" rel="noreferrer"
             style={{ ...tlacitko(), textDecoration: "none", display: "inline-block" }}>
            {t("realSlozka.inzeratOtevrit")}
          </a>
        )}
      </div>
    </article>
  );
}

/* ── PŘEDÁNÍ KONTAKTU ───────────────────────────────────────────────────── */

function RadekPredavky({ p, t, formatMoney, onOtevrit }) {
  const kdo = p.smer === "predal" ? t("realSlozka.predaniTy") : (p.kdo || t("realSlozka.predaniNekdo"));
  const komu = p.smer === "predal" ? (p.komu || t("realSlozka.predaniPoradce")) : t("realSlozka.predaniTy");
  return (
    <div style={{ paddingTop: 10, marginTop: 10, borderTop: "1px solid var(--line)" }}
         data-testid={`reality-predavka-${p.id}`}>
      <div style={{ fontSize: 13, color: "var(--ink)" }}>{`${kdo} → ${komu}`}</div>
      <div style={{ ...POPISEK, marginTop: 2 }}>
        {`${denLidsky(p.predano_at)} · ${p.kdy_okamzik} · ${p.co} · ${t(`realSlozka.predavkaStav.${p.stav}`)}`}
      </div>
      {/* Doklad, na základě čeho se kontakt předal. Bez souhlasu předávka
          vůbec nevznikne — tohle je jeho viditelný záznam. */}
      <div style={{ ...POPISEK, marginTop: 2 }}>
        {p.souhlas_cas
          ? `${t("realSlozka.souhlasDan")} ${denLidsky(p.souhlas_cas)}`
          : t("realSlozka.souhlasNeznamy")}
      </div>
      <div style={{ ...POPISEK, marginTop: 2 }}>
        {p.obchod
          ? `${t("realSlozka.vzniklObchod")}: ${p.obchod.title}${p.obchod.stav_label ? ` · ${p.obchod.stav_label}` : ""}`
          : p.vznikl_obchod
            ? t("realSlozka.vzniklObchodJinde")
            : t("realSlozka.vzniklObchodNe")}
      </div>
      <div style={{ ...POPISEK, marginTop: 2 }}>
        {p.odmena_nastavena
          ? `${t("realSlozka.odmena")}: ${p.odmena_sazba_pct} % ${p.odmena_zaklad_popis}${
              p.odmena_odhad != null ? ` ≈ ${formatMoney(p.odmena_odhad)}` : ""}`
          : t("realSlozka.odmenaNenastavena")}
      </div>
      {p.obchod && (
        <div style={RADKA}>
          <button type="button" style={tlacitko()} onClick={() => onOtevrit(`/obchody/${p.obchod.id}`)}>
            {t("realSlozka.otevritObchod")}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── CELEK ──────────────────────────────────────────────────────────────── */

export { prohlidkaKUkazani, stavRezervace };

export default function RealityVeSlozce({ prehled, onOtevrit, onNovaHypoteka }) {
  const { t, formatMoney } = useLanguage();
  if (!prehled) return null;
  const { prodej = [], koupe = [], predavky = [], hypoteky = [] } = prehled;
  if (!prodej.length && !koupe.length && !predavky.length) return null;

  return (
    <section data-testid="reality-ve-slozce" style={{ marginBottom: 4 }}>
      {prodej.length > 0 && (
        <>
          <h3 style={NADPIS_SEKCE}>{t("realSlozka.nadpisProdej")}</h3>
          {prodej.map((z) => (
            <KartaProdeje key={z.zakazka_id} z={z} t={t} formatMoney={formatMoney}
                          onOtevrit={onOtevrit} />
          ))}
        </>
      )}

      {koupe.length > 0 && (
        <>
          <h3 style={NADPIS_SEKCE}>{t("realSlozka.nadpisKoupe")}</h3>
          {koupe.map((k) => (
            <KartaKoupe key={k.deal_id || k.zakazka_id} k={k} hypoteky={hypoteky} t={t}
                        formatMoney={formatMoney} onOtevrit={onOtevrit}
                        onNovaHypoteka={onNovaHypoteka} />
          ))}
        </>
      )}

      {predavky.length > 0 && (
        <>
          <h3 style={NADPIS_SEKCE}>{t("realSlozka.predani")}</h3>
          <article style={{ ...KARTA, marginBottom: 12 }} data-testid="reality-predavky">
            <p style={{ margin: 0, ...POPISEK }}>{t("realSlozka.predaniSub")}</p>
            {predavky.map((p) => (
              <RadekPredavky key={p.id} p={p} t={t} formatMoney={formatMoney} onOtevrit={onOtevrit} />
            ))}
          </article>
        </>
      )}
    </section>
  );
}
